import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import { QuoteLineExpansion } from 'components/Quote/QuoteLineExpansion';
import React, { useMemo, useState } from 'react';
import { Column, useRowState, useSortBy, useTable } from 'react-table';
import { EditableTextField } from 'shared/components/EditableTextField';
import { getAdjustedExtPrice } from 'shared/helpers';
import { DollarCell } from 'shared/tables/DollarCell';
import { PercentCell } from 'shared/tables/PercentCell';
import { ColumnsQuoteLineTable, QuoteLine } from 'shared/types/quoteline';

const tableStyles: SxProps<Theme> = { overflow: 'auto', maxHeight: '100%' };

interface QuoteLineTableProps {
  lines: QuoteLine[];
  onReviseDiscount?: (lineNumber: number, newDiscount: number) => void;
  setIsExpanded: (index: number) => void;
}

export const QuoteLineTable = ({ lines, onReviseDiscount, setIsExpanded }: QuoteLineTableProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const data = useMemo(() => {
    return lines.map(l => ({
      ...l,
      ...l.calcResults,
      productNumber: l.product.item_number,
      productDescription: l.product.product_description,
      quantity: l.quantity,
      listPrice: parseFloat(l.product.list_price) || 0,
      extendedPrice: parseFloat(l.product.list_price) * l.quantity,
      revisedDiscount: l.revisedDiscount ?? l.calcResults?.recommendedDiscount ?? 0,
      adjustedExtendedPrice: getAdjustedExtPrice(l),
    }));
  }, [lines]);

  const columns = useMemo<Column<ColumnsQuoteLineTable>[]>(
    () => [
      {
        Header: 'Product',
        accessor: 'productNumber',
      },
      {
        Header: 'Product Description',
        accessor: 'productDescription',
      },
      {
        Header: 'Quantity',
        accessor: 'quantity',
      },
      {
        Header: 'List Price',
        accessor: 'listPrice',
        Cell: ({ row: { original } }) => <DollarCell value={original.listPrice} />,
      },
      {
        Header: 'Ext. Price',
        accessor: 'extendedPrice',
        Cell: ({ row: { original } }) => <DollarCell value={original.extendedPrice} />,
      },
      {
        Header: 'Maximum Allowable Discount',
        accessor: 'recommendedDiscount',
        Cell: ({ row: { original } }) => <PercentCell value={original.recommendedDiscount ?? 0} />,
      },
      {
        Header: 'Final Discount',
        accessor: 'revisedDiscount',
        Cell: ({ row: { original }, cell }) =>
          onReviseDiscount ? (
            <EditableTextField
              initialValue={original.revisedDiscount.toString() || '0'}
              onUpdate={(newRevisedDiscount: number) => {
                if (original.recommendedDiscount) {
                  if (newRevisedDiscount / 100 > original.recommendedDiscount) {
                    setOpenDialog(!openDialog);
                  }
                }

                onReviseDiscount(original.lineNumber, newRevisedDiscount / 100);
              }}
              type='percent'
            />
          ) : (
            <PercentCell value={original.revisedDiscount} />
          ),
      },
      {
        Header: 'Adjusted Ext. Price',
        accessor: 'adjustedExtendedPrice',
        Cell: ({ row: { original } }) => <DollarCell value={original.adjustedExtendedPrice} />,
      },
      {
        Header: '',
        accessor: 'isExpanded',
        Cell: cell => (
          <IconButton aria-label='expand row' size='small' onClick={() => setIsExpanded(cell.row.index)}>
            {cell.row.original.isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        ),
      },
    ],
    [onReviseDiscount, setIsExpanded, openDialog]
  );

  const tableInstance = useTable({ columns, data }, useSortBy, useRowState);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  return (
    <TableContainer sx={tableStyles}>
      <Table {...getTableProps()} stickyHeader>
        <TableHead>
          {headerGroups.map(headerGroup => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <TableCell {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                </TableCell>
              ))}
              <TableCell></TableCell>
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <React.Fragment key={`${row.original.productNumber}|${row.original.quantity}`}>
                <TableRow {...row.getRowProps()}>
                  {row.cells.map(cell => {
                    return <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>;
                  })}
                </TableRow>
                {row.original.isExpanded && <QuoteLineExpansion row={row} />}
              </React.Fragment>
            );
          })}
        </TableBody>
        <Dialog
          open={openDialog}
          onClose={() => {
            setOpenDialog(!openDialog);
          }}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogTitle id='alert-dialog-title'>{'Warning'}</DialogTitle>
          <DialogContent>
            <DialogContentText id='alert-dialog-description'>
              Final discount is above the maximum allowable.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenDialog(!openDialog);
              }}
              autoFocus
            >
              Agree
            </Button>
          </DialogActions>
        </Dialog>
      </Table>
    </TableContainer>
  );
};
